import { useEffect, useMemo, useState } from "react";
import { DoraLinkButton, DoraTextInput, DoraSelect } from "dora-shared/frontend";
import { Button, Dialog } from "@mui/material";
import { ClientsSelectWithRefresh } from "../ClientSelect";
import "./ManageClientCargoTemplates.scss";
import { CloseButton } from "../../../dispatching-planning/common/close-button";
import { useAppDispatch, useSelector } from "../../../../redux-store";
import { selectClients } from "../../../../ducks/data/clients/selectors";
import { selectOrderTemplatesForClientTeamAndSearch } from "../../../../ducks/data/client-order-templates/selectors";
import { loadClientCargoTemplates } from "../../../../ducks/data/client-order-templates";
import { useTranslation } from "react-i18next";
import ClientCargoTemplateRow from "./ClientCargoTemplateRow";
import { selectMyTeams } from "../../../../ducks/data/teams/selectors";

const templatesPerPage = 8;

const ManageClientCargoTemplates = () => {
  const { t } = useTranslation(["modals", "components"]);
  const [shouldShowDialog, setShouldShowDialog] = useState(false);
  const [selectedClientId, setSelectedClientId] = useState("");
  const teams = useSelector(selectMyTeams);
  const [selectedTeamId, setSelectedTeamId] = useState("");
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const clients = useSelector(selectClients);
  const templates = useSelector(
    selectOrderTemplatesForClientTeamAndSearch({
      clientId: selectedClientId,
      teamId: selectedTeamId,
      search,
    })
  );
  const dispatch = useAppDispatch();

  const templatesInView = useMemo(() => {
    const start = (page - 1) * templatesPerPage;
    const end = start + templatesPerPage;
    return templates.slice(start, end);
  }, [templates, page]);

  useEffect(() => {
    dispatch(loadClientCargoTemplates());
  }, [dispatch]);

  const teamsOptions = useMemo(
    () => teams.map((x) => ({ value: x.id, label: x.name })),
    [teams]
  );

  const totalPages =
    templates.length === 0 ? 0 : Math.ceil(templates.length / 8);

  return (
    <div>
      {shouldShowDialog && (
        <Dialog open onClose={() => setShouldShowDialog(false)}>
          <div className="manage-client-cargo-templates-dialog">
            <div className="manage-client-cargo-templates-dialog__title">
              <div>{t("modals:manageOrderTemplates.manageTitle")}</div>
              <CloseButton onClick={() => setShouldShowDialog(false)} />
            </div>
            <div className="manage-client-cargo-templates-dialog__subtitle">
              {t("modals:manageOrderTemplates.description")}
            </div>
            <div className="manage-client-cargo-templates-dialog__client-select">
              <ClientsSelectWithRefresh
                placeholder={t("modals:manageOrderTemplates.selectClient")}
                clients={clients}
                refreshButtonLabel={t("modals:manageOrderTemplates.refresh")}
                onChange={(clientId) => {
                  setSelectedClientId(clientId);
                  setSelectedTeamId("");
                  setPage(1);
                  setSearch("");
                }}
              />
            </div>
            {selectedClientId && (
              <div className="px-6 py-2 flex flex-col gap-2">
                <DoraSelect
                  options={teamsOptions}
                  placeholder={t("modals:manageOrderTemplates.selectTeam")}
                  fullWidth
                  onChange={(id) => {
                    setSelectedTeamId(id);
                    setPage(1);
                  }}
                />
                <DoraTextInput
                  onChange={(s) => {
                    setPage(1);
                    setSearch(s.toLowerCase().trim());
                  }}
                  placeholder={t("modals:manageOrderTemplates.searchTemplate")}
                ></DoraTextInput>
              </div>
            )}
            <div className="flex flex-col gap-2 mt-2">
              {templatesInView.length === 0 && selectedClientId && (
                <div className="text-gray-500 px-7">
                  {t("modals.manageOrderTemplates.noTemplatesFound")}
                </div>
              )}
              {templatesInView.map((template) => (
                <ClientCargoTemplateRow
                  template={template}
                  clientId={selectedClientId}
                  key={template.id}
                />
              ))}
            </div>
            <div className="manage-client-cargo-templates-dialog__footer">
              <div>
                {t("modals:manageSavedLocations.pagination", {
                  currentPage: page,
                  totalPages,
                })}
              </div>
              <div className="flex gap-3">
                <Button
                  variant="outlined"
                  onClick={() => setPage(page - 1)}
                  disabled={page === 1}
                >
                  {t("modals:manageSavedLocations.previousButton")}
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => setPage(page + 1)}
                  disabled={page === totalPages || totalPages === 0}
                >
                  {t("modals:manageSavedLocations.nextButton")}
                </Button>
              </div>
            </div>
          </div>
        </Dialog>
      )}
      <DoraLinkButton onClick={() => setShouldShowDialog(true)}>
        {t("modals:manageOrderTemplates.manageTitle")}
      </DoraLinkButton>
    </div>
  );
};

export default ManageClientCargoTemplates;
